// compare-harness — the PURE driver builder for the "Compare algorithms" surface
// (compare-activity, Trello m98B2mq7). No DOM, no runner: everything here is a string or a
// number, so the host-only spec (test/editor/compare-harness.test.js) can run the generated
// Python on host CPython and prove the counts.
//
// ── How a version is measured ──────────────────────────────────────────────────────────────
// ONE generated Python program per (version, action) is loaded into the hidden runner
// (hidden-runner.js `setSource` + `consoleRun`). It `exec`s the version's text under the
// filename `<compare>`, then for each ITEM (one input: a size, or one authored test) calls
// `functionName(*args)` under `sys.settrace`, counting `line` events for frames whose filename
// is `<compare>`:
//   * if the version has one or more lines whose trailing comment is exactly `# count`, only
//     events on those lines count (several marked lines SUM);
//   * otherwise EVERY executed line in the version counts (the fallback metric).
// The count is per call, never cumulative. Each item has its own budget — the 1,000,000 count
// cap, a line-event ceiling (3,000,000 for a run, 200,000 for a check) and a ~1.5 s wall budget
// checked every 10,000 events — and tripping any of them marks THAT item "capped" ("too many to
// count") and moves on to the next. Anything the version prints is captured with io.StringIO
// (truncated to 2,000 characters), never mixed into the result lines. Each finished item prints
// exactly one line `__PC_COMPARE__{json}` to the real stdout, smallest item first, so a whole-run
// timeout still leaves the finished items readable from the live stream.
//
// sys.settrace cannot see inside C builtins (max, sum, sorted, `in`, len …): work done by a
// builtin is not counted, and a single non-yielding builtin loop emits no events at all (the
// runner's own timeout is the backstop there).

export var RESULT_PREFIX = '__PC_COMPARE__';
export var COUNT_CAP = 1000000;
export var RUN_EVENT_CAP = 3000000;
export var CHECK_EVENT_CAP = 200000;
export var WALL_SECONDS = 1.5;
export var OUTPUT_CAP = 2000;
export var MAX_SIZE = 2000;
export var MAX_CUSTOM_LIST = 1000;

// A trailing `# count` comment, case-insensitive, optional spaces, nothing after — on a line
// that also carries code (a comment-only line never executes, so it could never count).
var COUNT_MARK = /^(\s*[^\s#].*?)#\s*count\s*$/i;

/** 1-based line numbers of the version's `# count` lines (empty ⇒ the all-lines fallback). */
export function countedLines(code) {
  var out = [];
  String(code || '').replace(/\r\n?/g, '\n').split('\n').forEach(function (line, i) {
    if (COUNT_MARK.test(line)) { out.push(i + 1); }
  });
  return out;
}

/** Does this version use marked lines (true) or the all-lines fallback (false)? */
export function usesMarkedLines(code) {
  return countedLines(code).length > 0;
}

// Python string literal for arbitrary text: JSON is a valid Python str literal for every
// character JSON escapes (\n, \", \\, \uXXXX), so the text round-trips exactly.
function pyStr(text) {
  return JSON.stringify(String(text == null ? '' : text));
}

/**
 * Build the driver program.
 *
 *   opts.code          the version's source text
 *   opts.functionName  the function to call
 *   opts.items         [{ id, args }] — `args` a JSON array (already smallest-first)
 *   opts.eventCap      RUN_EVENT_CAP (default) or CHECK_EVENT_CAP
 *   opts.countCap      COUNT_CAP (default)
 *   opts.wallSeconds   WALL_SECONDS (default)
 *   opts.outputCap     OUTPUT_CAP (default)
 *
 * Every value reaches Python as a JSON string decoded with `json.loads`, so no author text is
 * ever spliced into code.
 */
export function buildDriver(opts) {
  var o = opts || {};
  var items = (Array.isArray(o.items) ? o.items : []).map(function (it) {
    return { id: String(it && it.id), args: Array.isArray(it && it.args) ? it.args : [] };
  });
  var cfg = {
    fn: String(o.functionName || ''),
    marked: countedLines(o.code),
    items: items,
    countCap: typeof o.countCap === 'number' ? o.countCap : COUNT_CAP,
    eventCap: typeof o.eventCap === 'number' ? o.eventCap : RUN_EVENT_CAP,
    wall: typeof o.wallSeconds === 'number' ? o.wallSeconds : WALL_SECONDS,
    outCap: typeof o.outputCap === 'number' ? o.outputCap : OUTPUT_CAP
  };
  return [
    'import sys, json, io, time, contextlib',
    '__pc_src = ' + pyStr(o.code),
    '__pc_cfg = json.loads(' + pyStr(JSON.stringify(cfg)) + ')',
    '__pc_marked = set(__pc_cfg["marked"])',
    '',
    'class __PcCapped(BaseException):',
    '    pass',
    '',
    'def __pc_jsonable(v, depth=0):',
    '    if depth > 6:',
    '        raise TypeError("too deep")',
    '    if v is None or isinstance(v, (bool, str)):',
    '        return v',
    '    if isinstance(v, int):',
    '        return v',
    '    if isinstance(v, float):',
    '        if v != v or v in (float("inf"), float("-inf")):',
    '            raise TypeError("not a finite number")',
    '        return v',
    '    if isinstance(v, (list, tuple)):',
    '        return [__pc_jsonable(x, depth + 1) for x in v]',
    '    if isinstance(v, dict):',
    '        out = {}',
    '        for k, x in v.items():',
    '            if not isinstance(k, str):',
    '                raise TypeError("dictionary keys must be text")',
    '            out[k] = __pc_jsonable(x, depth + 1)',
    '        return out',
    '    raise TypeError("a " + type(v).__name__ + " value")',
    '',
    'def __pc_emit(obj):',
    '    print("' + RESULT_PREFIX + '" + json.dumps(obj), flush=True)',
    '',
    'def __pc_run(item):',
    '    ns = {"__name__": "__compare__"}',
    '    buf = io.StringIO()',
    '    st = {"count": 0, "events": 0, "t0": 0.0, "capped": False}',
    '    marked = __pc_marked',
    '    count_cap = __pc_cfg["countCap"]',
    '    event_cap = __pc_cfg["eventCap"]',
    '    wall = __pc_cfg["wall"]',
    '    def local(frame, event, arg):',
    '        if event == "line":',
    '            st["events"] += 1',
    '            if not marked or frame.f_lineno in marked:',
    '                st["count"] += 1',
    '            if st["count"] > count_cap or st["events"] > event_cap:',
    '                st["capped"] = True',
    '                raise __PcCapped()',
    '            if st["events"] % 10000 == 0 and time.monotonic() - st["t0"] > wall:',
    '                st["capped"] = True',
    '                raise __PcCapped()',
    '        return local',
    '    def glob(frame, event, arg):',
    '        if frame.f_code.co_filename == "<compare>":',
    '            return local',
    '        return None',
    '    status, err, ret, has_ret = "ok", "", None, False',
    '    try:',
    '        code = compile(__pc_src, "<compare>", "exec")',
    '        with contextlib.redirect_stdout(buf):',
    '            exec(code, ns)',
    '    except BaseException as e:',
    '        if isinstance(e, KeyboardInterrupt):',
    '            raise',
    '        return {"id": item["id"], "status": "error", "steps": None, "error": type(e).__name__ + ": " + str(e), "output": "", "outputTruncated": False, "value": None, "returned": None}',
    '    fn = ns.get(__pc_cfg["fn"])',
    '    if not callable(fn):',
    '        return {"id": item["id"], "status": "error", "steps": None, "error": "NameError: " + __pc_cfg["fn"] + "() is not defined", "output": "", "outputTruncated": False, "value": None, "returned": None}',
    '    try:',
    '        with contextlib.redirect_stdout(buf):',
    '            st["t0"] = time.monotonic()',
    '            sys.settrace(glob)',
    '            try:',
    '                ret = fn(*item["args"])',
    '                has_ret = True',
    '            finally:',
    '                sys.settrace(None)',
    '    except __PcCapped:',
    '        st["capped"] = True',
    '    except BaseException as e:',
    '        if isinstance(e, KeyboardInterrupt):',
    '            raise',
    '        if not st["capped"]:',
    '            status = "error"',
    '            err = type(e).__name__ + ": " + str(e)',
    '    if st["capped"]:',
    '        status = "capped"',
    '    text = buf.getvalue()',
    '    out_cap = __pc_cfg["outCap"]',
    '    truncated = len(text) > out_cap',
    '    result = {"id": item["id"], "status": status, "steps": (st["count"] if status == "ok" else None),',
    '              "events": st["events"], "error": err, "output": text[:out_cap], "outputTruncated": truncated,',
    '              "value": None, "returned": None, "valueError": ""}',
    '    if status == "ok":',
    '        if has_ret and ret is not None:',
    '            result["returned"] = repr(ret)[:out_cap]',
    '        try:',
    '            result["value"] = __pc_jsonable(ret)',
    '        except TypeError as e:',
    '            result["valueError"] = str(e)',
    '    return result',
    '',
    'for __pc_item in __pc_cfg["items"]:',
    '    __pc_emit(__pc_run(__pc_item))',
    ''
  ].join('\n');
}

/** One result line → the parsed result object, or null (not a result line / bad JSON). */
export function parseResultLine(line) {
  var s = String(line == null ? '' : line).replace(/\r$/, '');
  var at = s.indexOf(RESULT_PREFIX);
  if (at === -1) { return null; }
  try {
    var obj = JSON.parse(s.slice(at + RESULT_PREFIX.length));
    return (obj && typeof obj === 'object' && typeof obj.id === 'string') ? obj : null;
  } catch (e) { return null; }
}

/**
 * All result lines in a stdout blob → `{ [id]: result }` (the LAST line for an id wins). Any
 * other text — stray output that escaped the capture — is ignored.
 */
export function parseResults(stdout) {
  var out = {};
  String(stdout || '').split('\n').forEach(function (line) {
    var r = parseResultLine(line);
    if (r) { out[r.id] = r; }
  });
  return out;
}

/**
 * Stdout arrives in ARBITRARY chunks (a result line may be split mid-token, a lone "\n" may
 * arrive on its own, the last line may have no terminator). `push(chunk)` returns the COMPLETE
 * lines that chunk finished; `flush()` returns the trailing partial line (if any) at settle.
 */
export function createLineAssembler() {
  var buffer = '';
  return {
    push: function (chunk) {
      buffer += String(chunk == null ? '' : chunk);
      var parts = buffer.split('\n');
      buffer = parts.pop();
      return parts;
    },
    flush: function () {
      var rest = buffer;
      buffer = '';
      return rest ? [rest] : [];
    }
  };
}

/** The whole-run timeout handed to `consoleRun`: 4000 + 1500 × items (ms). */
export function runTimeoutMs(itemCount) {
  var n = Math.max(1, Number(itemCount) || 1);
  return 4000 + 1500 * n;
}

// ── inputs ─────────────────────────────────────────────────────────────────────────────────

/**
 * A deterministic list of `n` integers. `random-ints` uses a fixed Park–Miller generator
 * SEEDED WITH `n`, values 0..999 — so re-running a size gives identical numbers every time.
 * `ascending` is 1..n; `descending` n..1.
 */
export function makeList(generator, n) {
  var size = Math.max(0, Math.min(MAX_SIZE, Math.floor(Number(n) || 0)));
  var out = [];
  var i;
  if (generator === 'ascending') {
    for (i = 1; i <= size; i++) { out.push(i); }
    return out;
  }
  if (generator === 'descending') {
    for (i = size; i >= 1; i--) { out.push(i); }
    return out;
  }
  var state = (size % 2147483646) + 1;
  for (i = 0; i < size; i++) {
    state = (state * 48271) % 2147483647;
    out.push(state % 1000);
  }
  return out;
}

/**
 * The student's own list: up to 1000 comma-separated whole numbers, optional surrounding
 * brackets. Parsed with `Number` + an integer check — never eval, never a Python parse.
 * Returns `{ ok: true, list }` or `{ ok: false, error }` (an empty box is `{ ok: true, list: null }`).
 */
export function parseCustomList(text) {
  var raw = String(text == null ? '' : text).trim();
  if (!raw) { return { ok: true, list: null }; }
  if (raw.charAt(0) === '[' && raw.charAt(raw.length - 1) === ']') { raw = raw.slice(1, -1).trim(); }
  if (!raw) { return { ok: false, error: 'Type at least one number.' }; }
  var parts = raw.split(',');
  if (parts.length > MAX_CUSTOM_LIST) {
    return { ok: false, error: 'Use ' + MAX_CUSTOM_LIST + ' numbers or fewer.' };
  }
  var list = [];
  for (var i = 0; i < parts.length; i++) {
    var p = parts[i].trim();
    if (!/^-?\d+$/.test(p)) {
      return { ok: false, error: 'Use whole numbers separated by commas, like 4, 9, 2.' };
    }
    var n = Number(p);
    if (!Number.isSafeInteger(n)) { return { ok: false, error: 'One of those numbers is too big.' }; }
    list.push(n);
  }
  return { ok: true, list: list };
}

/** The call arguments for one live-run or sweep size: `fn(n)` or `fn(list)`. */
export function argsForSize(input, n) {
  var kind = input && input.kind === 'list' ? 'list' : 'number';
  var size = Math.max(0, Math.min(MAX_SIZE, Math.floor(Number(n) || 0)));
  if (kind === 'number') { return [size]; }
  return [makeList(input && input.generator, size)];
}
